"use client";

import React from "react";

export interface FilterOption {
  therapyType?: string[];
  languages?: string[];
  areasOfFocus: string[];
  provinces: string[];
  therapistTier?: string[];
  availability: string[];
  serviceFormat?: string[];
  modalities: string[];
}

export interface FilterSidebarProps {
  filters: FilterOption;
  filterOptions: {
    therapyType?: string[];
    languages?: string[];
    areasOfFocus: Record<string, string[]>;
    provinces: string[];
    therapistTier?: string[];
    availability: string[];
    serviceFormat?: string[];
    modalities: string[];
  };
  focusSearchTerm: string;
  setFocusSearchTerm: (term: string) => void;
  toggleFilter: (category: keyof FilterOption, value: string) => void;
  activeFilterCount: number;
  onClearAll: () => void;
}

export function FilterSidebar({
  filters,
  filterOptions,
  focusSearchTerm,
  setFocusSearchTerm,
  toggleFilter,
  activeFilterCount,
  onClearAll
}: FilterSidebarProps) {
  // Get all specializations in a flat list and sort alphabetically
  const allSpecializations = Object.values(filterOptions.areasOfFocus).flat().sort((a, b) => a.localeCompare(b));
  
  // Filter specializations based on search term
  const filteredSpecializations = allSpecializations.filter(spec =>
    spec.toLowerCase().includes(focusSearchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 sticky top-4">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Find Your Match</h2>
          {activeFilterCount > 0 && (
            <span className="bg-gradient-to-r from-primary-100 to-secondary-100 text-primary-700 text-xs px-2.5 py-1 rounded-full font-medium">
              {activeFilterCount}
            </span>
          )}
        </div>
      </div>
      
      <div className="p-5 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
        {/* Therapy Type */}
        {filterOptions.therapyType && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Therapy Type</h3>
            <div className="space-y-0.5">
              {filterOptions.therapyType.map(type => (
                <label
                  key={type}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={filters.therapyType?.includes(type) || false}
                    onChange={() => toggleFilter('therapyType' as keyof FilterOption, type)}
                    className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Languages */}
        {filterOptions.languages && filterOptions.languages.length > 0 && (
          <div className="pt-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Language</h3>
            <div className="space-y-0.5">
              {filterOptions.languages.map(language => (
                <label
                  key={language}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={filters.languages?.includes(language) || false}
                    onChange={() => toggleFilter('languages' as keyof FilterOption, language)}
                    className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-700">{language}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Areas of Focus - Searchable */}
        <div className="flex flex-col max-h-[280px]">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex-shrink-0">What brings you here?</h3>
          
          {/* Search input */}
          <div className="relative mb-3">
            <input
              type="text"
              value={focusSearchTerm}
              onChange={(e) => setFocusSearchTerm(e.target.value)}
              placeholder="Search specializations..."
              className="w-full px-3 py-2 pr-8 text-sm text-gray-900 placeholder-gray-500 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
            {focusSearchTerm && (
              <button
                onClick={() => setFocusSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Specializations list */}
          <div 
            className="space-y-0.5 overflow-y-auto pr-1"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 #f3f4f6'
            }}
          >
            {filteredSpecializations.length > 0 ? (
              filteredSpecializations.map(spec => (
                <label
                  key={spec}
                  className="flex items-start gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={filters.areasOfFocus.includes(spec)}
                    onChange={() => toggleFilter('areasOfFocus', spec)}
                    className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-[11px] text-gray-600 leading-snug">{spec}</span>
                </label>
              ))
            ) : (
              <p className="text-[11px] text-gray-400 text-center py-2">No results found</p>
            )}
          </div>
          
          {/* Selected count */}
          {filters.areasOfFocus.length > 0 && (
            <p className="text-[10px] text-gray-500 mt-1">
              {filters.areasOfFocus.length} selected
            </p>
          )}
        </div>

        {/* Therapist Type */}
        {filterOptions.therapistTier && filterOptions.therapistTier.length > 0 && (
          <div className="pt-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Therapist Type</h3>
            <div className="space-y-0.5">
              {filterOptions.therapistTier.map(tier => (
                <label
                  key={tier}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={filters.therapistTier?.includes(tier) || false}
                    onChange={() => toggleFilter('therapistTier', tier)}
                    className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-700">{tier}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Availability */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Availability</h3>
          <div className="space-y-0.5">
            {filterOptions.availability.map(avail => (
              <label
                key={avail}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={filters.availability.includes(avail)}
                  onChange={() => toggleFilter('availability', avail)}
                  className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-xs text-gray-700">{avail}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Location */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Location</h3>
          <div className="space-y-0.5 max-h-32 overflow-y-auto pr-1">
            {filterOptions.provinces.map(province => (
              <label
                key={province}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={filters.provinces.includes(province)}
                  onChange={() => toggleFilter('provinces', province)}
                  className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-xs text-gray-700">{province}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Service Format */}
        {filterOptions.serviceFormat && filterOptions.serviceFormat.length > 0 && (
          <div className="pt-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Service Format</h3>
            <div className="space-y-0.5">
              {filterOptions.serviceFormat.map(format => (
                <label
                  key={format}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={filters.serviceFormat?.includes(format) || false}
                    onChange={() => toggleFilter('serviceFormat' as keyof FilterOption, format)}
                    className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-700">{format}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Modalities */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Approach</h3>
          <div className="space-y-0.5 max-h-40 overflow-y-auto pr-1">
            {filterOptions.modalities.map(modality => (
              <label
                key={modality}
                className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={filters.modalities.includes(modality)}
                  onChange={() => toggleFilter('modalities', modality)}
                  className="w-3 h-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
                />
                <span className="text-xs text-gray-700 leading-snug">{modality}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      
      {/* Clear Filters - Fixed at bottom */}
      {activeFilterCount > 0 && (
        <div className="p-5 border-t border-gray-100">
          <button
            onClick={onClearAll}
            className="w-full py-2.5 text-center text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 rounded-xl transition-all"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}